import React from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Typography, Box } from '@mui/material';
import { Translate as TranslateIcon, Check as CheckIcon } from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Language } from '../../../translations/translations';
import styled from 'styled-components';

const StyledIconButton = styled(IconButton)`
  color: white;
  margin-left: 1rem;
  background: rgba(255, 255, 255, 0.15);
  width: 40px;
  height: 40px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  svg {
    font-size: 22px;
  }
`;

const StyledMenuItem = styled(MenuItem)<{ selected?: boolean }>`
  min-width: 140px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  ${({ selected, theme }) => selected && `
    background-color: ${theme.palette.primary.light}15 !important;
    
    .MuiSvgIcon-root {
      color: ${theme.palette.primary.main};
    }
  `}
`;

const LanguageText = styled(Typography)`
  font-size: 0.95rem;
  font-weight: 500;
`;

const LanguageCode = styled(Typography)`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-transform: uppercase;
  margin-left: 8px;
`;

const LanguageSelector: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    handleClose();
  };

  const languages = [
    { code: 'en', label: t('common.english') },
    { code: 'zh', label: t('common.chinese') },
    { code: 'ru', label: t('common.russian') },
    { code: 'de', label: t('common.german') },
    { code: 'id', label: t('common.indonesian') },
    { code: 'ko', label: t('common.korean') },
    { code: 'ja', label: t('common.japanese') },
    { code: 'th', label: t('common.thai') }
  ];

  return (
    <>
      <Tooltip title={t('common.language')}>
        <StyledIconButton onClick={handleClick}>
          <TranslateIcon sx={{ color: 'white' }} />
        </StyledIconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 180
          }
        }}
      >
        {languages.map(({ code, label }) => (
          <StyledMenuItem
            key={code}
            onClick={() => handleLanguageChange(code as Language)}
            selected={language === code}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LanguageText>{label}</LanguageText>
              <LanguageCode>({code})</LanguageCode>
            </Box>
            {language === code && <CheckIcon fontSize="small" />}
          </StyledMenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector; 